/**
 * Does this listing belong to one of the categories the shopper selected?
 *
 * `selected` is searchParamsAtom.categories: one entry per checked category,
 * each carrying the sub-categories checked under it. Checking a whole
 * category fills that list with all of its sub-categories, so there is no
 * stored difference between "the whole category" and "every sub-category".
 *
 * The subtlety is listings with no sub-category of their own. A category is
 * required when assigning one, a sub-category is not, and a Shopify listing
 * with none falls back to the Shopify product type ("Trading Cards", say),
 * which is never one of a marketplace category's own sub-categories. A
 * local listing left blank simply carries an empty string. Matching only on
 * sub-category hid both from the very category they were assigned to, which
 * made a seller's own categorisation look like it had not saved.
 *
 * So an unclassified listing matches on its category alone. It stays visible
 * even when the shopper has narrowed to some sub-categories, which is the
 * forgiving side of an ambiguous case: the listing really is in that
 * category, it just doesn't say where within it.
 */
export const hasSubCategory = (listing) => {
    // For a Shopify listing the fallback has already overwritten
    // sub_category by the time it reaches here, so the flag set alongside it
    // is the only truthful signal. Local listings carry an empty string, and
    // listings normalized before the flag existed leave it undefined, which
    // must not be read as "unclassified".
    if (listing?.sub_category_assigned === false) return false
    return Boolean(String(listing?.sub_category || '').trim())
}

export const matchesCategoryFilter = (listing, selected = []) => {
    if (!listing) return false
    if (!Array.isArray(selected) || selected.length === 0) return true

    return selected.some((entry) => {
        if (entry?.category !== listing.category) return false
        if ((entry.subCategories || []).includes(listing.sub_category)) return true
        return !hasSubCategory(listing)
    })
}
